import { useRef, useState } from 'react';
import api from '../../../API';
import { girderThumbnailUrl } from '../../../utils/girderThumbnailUrl';
import { useProjectUpload } from '../useProjectUpload';
import OrderedListControls, { AlphabetizeButton } from '../hub/OrderedListControls';
import { IconPencil } from '../hub/HubRowActionIcons';
import { getDescendantIds } from './treeUtils';
import styles from '../ProjectEditForm.module.css';

/**
 * ItemGroup — recursive folder node for project items/subfolders.
 */
export default function ItemGroup({
    folderId,
    projectFolderId,
    label,
    groupItems,
    childFolders = [],
    depth = 0,
    checkedItems,
    onToggle,
    onToggleAll,
    moveOptions,
    onSingleMove,
    onDeleteItem,
    onDeleteSubfolder,
    onMoveSubfolder,
    onAddItems,
    onCreateSubfolder,
    onUploadDone,
    onPreviewItem,
    onRenameItem,
    onReorderInFolder,
    onAlphabetizeFolder,
    orderBusy = false,
    settingsLocked = false,
    itemGroups,
    deleteSubfolderHandler,
    moveSubfolderHandler,
    subfoldersTree = [],
}) {
    const [creatingSubfolder, setCreatingSubfolder] = useState(false);
    const [newSubfolderName, setNewSubfolderName] = useState('');
    const [creating, setCreating] = useState(false);
    const [createError, setCreateError] = useState(null);

    const uploadInputRef = useRef(null);
    const { uploads, uploading, uploadFiles, clearUploads } = useProjectUpload(folderId, onUploadDone);

    const allChecked =
        groupItems.length > 0 && groupItems.every((i) => checkedItems.has(String(i._id)));
    const canReorder = !settingsLocked && typeof onReorderInFolder === 'function';
    const showAlphabetize = canReorder && groupItems.length >= 2;

    function handleCreateSubfolder() {
        const name = newSubfolderName.trim();
        if (!name) return;
        setCreating(true);
        setCreateError(null);
        onCreateSubfolder(folderId, name)
            .then(() => {
                setNewSubfolderName('');
                setCreatingSubfolder(false);
            })
            .catch((e) => setCreateError(e.message || 'Failed to create subfolder.'))
            .finally(() => setCreating(false));
    }

    const indentStyle = depth > 0 ? { marginLeft: depth * 16 } : {};

    return (
        <div className={styles.itemGroup} style={indentStyle}>
            <div className={styles.itemGroupHeader}>
                <input
                    type="checkbox"
                    className={styles.groupCheckbox}
                    checked={allChecked}
                    onChange={onToggleAll}
                    title="Select all in this group"
                />
                <span className={styles.itemGroupLabel}>{label}</span>
                {showAlphabetize ? (
                    <AlphabetizeButton
                        onClick={() => onAlphabetizeFolder(folderId, groupItems)}
                        disabled={orderBusy}
                    />
                ) : null}
                <a
                    href={`/dsa#folder/${folderId}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={styles.dsaPill}
                    title="Open in default Girder UI"
                >
                    DSA
                </a>
                <div className={styles.itemGroupActions}>
                    {onMoveSubfolder && (() => {
                        const descendants = getDescendantIds(folderId, subfoldersTree);
                        const validOpts = moveOptions.filter(
                            (opt) => opt.id !== folderId && !descendants.has(opt.id)
                        );
                        return (
                            <select
                                className={styles.singleMoveSelect}
                                value=""
                                onChange={(e) => { if (e.target.value) onMoveSubfolder(e.target.value); }}
                                title="Move subfolder to…"
                            >
                                <option value="">Move to…</option>
                                {validOpts.map((opt) => (
                                    <option key={opt.id} value={opt.id}>{opt.name}</option>
                                ))}
                            </select>
                        );
                    })()}
                    <button
                        type="button"
                        className={styles.itemGroupActionButton}
                        onClick={() => onAddItems(folderId)}
                        title="Add items to this folder"
                    >
                        + Add items
                    </button>
                    <button
                        type="button"
                        className={styles.itemGroupActionButton}
                        onClick={() => setCreatingSubfolder(true)}
                        title="Create a subfolder here"
                    >
                        + Subfolder
                    </button>
                    <button
                        type="button"
                        className={styles.itemGroupActionButton}
                        onClick={() => uploadInputRef.current?.click()}
                        disabled={uploading}
                        title="Upload files into this folder"
                    >
                        ⬆ Upload
                    </button>
                    <input
                        ref={uploadInputRef}
                        type="file"
                        multiple
                        style={{ display: 'none' }}
                        onChange={(e) => {
                            if (e.target.files?.length) uploadFiles(e.target.files);
                            e.target.value = '';
                        }}
                    />
                    {onDeleteSubfolder && (
                        <button
                            type="button"
                            className={styles.deleteItemButton}
                            onClick={onDeleteSubfolder}
                            title="Delete this subfolder"
                        >
                            ✕
                        </button>
                    )}
                </div>
            </div>

            {uploads.length > 0 && (
                <div className={styles.uploadProgressList}>
                    {uploads.map((u) => (
                        <div key={u.name} className={styles.uploadProgressRow}>
                            <span className={styles.uploadFileName} title={u.name}>{u.name}</span>
                            {u.error ? (
                                <span className={styles.uploadError}>{u.error}</span>
                            ) : u.progress === 100 ? (
                                <span className={styles.uploadDone}>✓</span>
                            ) : (
                                <div className={styles.uploadBarTrack}>
                                    <div
                                        className={styles.uploadBarFill}
                                        style={{ width: `${u.progress}%` }}
                                    />
                                </div>
                            )}
                        </div>
                    ))}
                    {!uploading && (
                        <button
                            type="button"
                            className={styles.uploadDismissButton}
                            onClick={clearUploads}
                        >
                            Dismiss
                        </button>
                    )}
                </div>
            )}

            {creatingSubfolder && (
                <div className={styles.inlineSubfolderCreate}>
                    <input
                        type="text"
                        className={styles.subfolderInput}
                        value={newSubfolderName}
                        onChange={(e) => setNewSubfolderName(e.target.value)}
                        placeholder="New subfolder name…"
                        autoFocus
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') handleCreateSubfolder();
                            if (e.key === 'Escape') { setCreatingSubfolder(false); setNewSubfolderName(''); }
                        }}
                    />
                    <button
                        type="button"
                        className={styles.createSubfolderButton}
                        onClick={handleCreateSubfolder}
                        disabled={!newSubfolderName.trim() || creating}
                    >
                        {creating ? 'Creating…' : 'Create'}
                    </button>
                    <button
                        type="button"
                        className={styles.cancelAddButton}
                        onClick={() => { setCreatingSubfolder(false); setNewSubfolderName(''); }}
                    >
                        Cancel
                    </button>
                    {createError && (
                        <span className={styles.inlineError}>{createError}</span>
                    )}
                </div>
            )}

            {groupItems.length === 0 && childFolders.length === 0 && (
                <p className={styles.emptyGroupState}>Empty.</p>
            )}

            {groupItems.map((item, index) => {
                const iid = String(item._id);
                return (
                    <div
                        key={iid}
                        className={`${styles.itemRow} ${checkedItems.has(iid) ? styles.itemRowChecked : ''}`}
                    >
                        <input
                            type="checkbox"
                            className={styles.itemCheckbox}
                            checked={checkedItems.has(iid)}
                            onChange={() => onToggle(iid)}
                        />
                        <img
                            src={girderThumbnailUrl(api, iid)}
                            alt="preview"
                            className={styles.itemThumbnail}
                            onClick={(e) => { e.stopPropagation(); onPreviewItem?.(iid, item.name); }}
                            onError={(e) => { e.currentTarget.style.display = 'none'; }}
                        />
                        <span className={styles.itemName}>{item.name}</span>
                        {canReorder ? (
                            <OrderedListControls
                                canMoveUp={index > 0}
                                canMoveDown={index < groupItems.length - 1}
                                disabled={orderBusy}
                                onMoveUp={() => onReorderInFolder(folderId, groupItems, index, index - 1)}
                                onMoveDown={() => onReorderInFolder(folderId, groupItems, index, index + 1)}
                            />
                        ) : null}
                        {!settingsLocked && onRenameItem ? (
                            <button
                                type="button"
                                className={styles.itemRenameBtn}
                                title="Rename item"
                                aria-label={`Rename ${item.name}`}
                                onClick={() => onRenameItem(iid, item.name)}
                            >
                                <IconPencil />
                            </button>
                        ) : null}
                        <a
                            href={`/dsa#item/${iid}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={styles.dsaPill}
                            title="Open in default Girder UI"
                        >
                            DSA
                        </a>
                        <select
                            className={styles.singleMoveSelect}
                            value=""
                            onChange={(e) => { if (e.target.value) onSingleMove(iid, e.target.value); }}
                            title="Move to…"
                        >
                            <option value="">Move to…</option>
                            {moveOptions
                                .filter((opt) => opt.id !== folderId)
                                .map((opt) => (
                                    <option key={opt.id} value={opt.id}>{opt.name}</option>
                                ))}
                        </select>
                        <button
                            type="button"
                            className={styles.deleteItemButton}
                            onClick={() => onDeleteItem(iid, item.name)}
                            title="Delete item"
                        >
                            ✕
                        </button>
                    </div>
                );
            })}

            {childFolders.map((child) => {
                const childId = String(child._id);
                const childItems = itemGroups[childId] || [];
                return (
                    <ItemGroup
                        key={childId}
                        folderId={childId}
                        projectFolderId={projectFolderId}
                        label={child.name}
                        groupItems={childItems}
                        childFolders={child.children || []}
                        depth={depth + 1}
                        checkedItems={checkedItems}
                        onToggle={onToggle}
                        onToggleAll={() => {
                            const ids = childItems.map((i) => String(i._id));
                            const allCheckedNow = ids.length > 0 && ids.every((id) => checkedItems.has(id));
                            ids.forEach((id) => {
                                if (allCheckedNow ? checkedItems.has(id) : !checkedItems.has(id)) {
                                    onToggle(id);
                                }
                            });
                        }}
                        moveOptions={moveOptions}
                        onSingleMove={onSingleMove}
                        onDeleteItem={onDeleteItem}
                        onDeleteSubfolder={deleteSubfolderHandler ? deleteSubfolderHandler(childId, child.name) : undefined}
                        onMoveSubfolder={moveSubfolderHandler ? moveSubfolderHandler(childId) : undefined}
                        onAddItems={onAddItems}
                        onCreateSubfolder={onCreateSubfolder}
                        onUploadDone={onUploadDone}
                        onPreviewItem={onPreviewItem}
                        onRenameItem={onRenameItem}
                        onReorderInFolder={onReorderInFolder}
                        onAlphabetizeFolder={onAlphabetizeFolder}
                        orderBusy={orderBusy}
                        settingsLocked={settingsLocked}
                        itemGroups={itemGroups}
                        deleteSubfolderHandler={deleteSubfolderHandler}
                        moveSubfolderHandler={moveSubfolderHandler}
                        subfoldersTree={subfoldersTree}
                    />
                );
            })}
        </div>
    );
}
